import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import usePageMeta from "@/hooks/usePageMeta";
import { worksList } from "@/mocks/works";

const types = ["全部", "电影", "电视剧", "综艺"];
const statuses = ["全部", "上映", "未上映"];
const yearRanges = [
  { label: "全部年份", min: 0, max: 9999 },
  { label: "2025 - 2026", min: 2025, max: 2026 },
  { label: "2023 - 2024", min: 2023, max: 2024 },
  { label: "2022 及以前", min: 0, max: 2022 },
];

const PAGE_STEP = 6;

export default function WorksPage() {
  const [type, setType] = useState("全部");
  const [status, setStatus] = useState("全部");
  const [rangeIndex, setRangeIndex] = useState(0);
  const [visible, setVisible] = useState(PAGE_STEP);

  usePageMeta({
    title: "大岛优香影视作品全集",
    description:
      "汇总大岛优香参演的电影、电视剧与综艺作品，涵盖剧情简介、角色信息与年份筛选，全面了解她的影视作品脉络。",
    keywords: "大岛优香影视作品,电影,电视剧,综艺,作品列表",
  });

  const filtered = useMemo(() => {
    const range = yearRanges[rangeIndex];
    return worksList
      .filter((w) => type === "全部" || w.type === type)
      .filter((w) => status === "全部" || w.status === status)
      .filter((w) => w.year >= range.min && w.year <= range.max)
      .sort((a, b) => b.year - a.year);
  }, [type, status, rangeIndex]);

  const shown = filtered.slice(0, visible);

  const reset = () => {
    setType("全部");
    setStatus("全部");
    setRangeIndex(0);
    setVisible(PAGE_STEP);
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
        <Link to="/" className="hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
        <span className="text-foreground-800">作品一览</span>
      </nav>

      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
          作品一览
        </h1>
        <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
          收录大岛优香参与的影视作品，可按作品类型、年份区间与上映状态筛选，
          点击任意卡片即可查看作品详情与角色介绍。
        </p>
      </div>

      {/* Filter bar */}
      <div className="bg-background-100 border border-background-200 rounded-lg p-5 mb-8 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <span className="text-sm text-foreground-600 w-16 shrink-0">类型</span>
          <div className="flex flex-wrap gap-2">
            {types.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setType(t);
                  setVisible(PAGE_STEP);
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  type === t
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <span className="text-sm text-foreground-600 w-16 shrink-0">年份</span>
          <div className="flex flex-wrap gap-2">
            {yearRanges.map((r, i) => (
              <button
                key={r.label}
                type="button"
                onClick={() => {
                  setRangeIndex(i);
                  setVisible(PAGE_STEP);
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  rangeIndex === i
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-50 text-foreground-700 border border-background-200 hover:border-accent-300"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <span className="text-sm text-foreground-600 w-16 shrink-0">状态</span>
          <div className="flex flex-wrap gap-2">
            {statuses.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setStatus(s);
                  setVisible(PAGE_STEP);
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  status === s
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 border border-background-200 hover:border-secondary-300"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={reset}
            className="md:ml-auto text-sm text-foreground-500 hover:text-primary-600 whitespace-nowrap cursor-pointer flex items-center gap-1"
          >
            <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
            重置筛选
          </button>
        </div>
      </div>

      {shown.length > 0 ? (
        <>
          <p className="text-sm text-foreground-500 mb-4">
            共 <span className="text-foreground-900 font-medium">{filtered.length}</span> 部作品
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop>
            {shown.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>

          {visible < filtered.length && (
            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_STEP)}
                className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                加载更多作品
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="bg-background-100 border border-background-200 rounded-lg py-16 text-center">
          <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center mx-auto mb-4">
            <i className="ri-film-line text-2xl" />
          </span>
          <p className="font-heading font-bold text-foreground-800 mb-1">
            未找到符合条件的作品
          </p>
          <p className="text-sm text-foreground-500 mb-5">
            试着放宽筛选条件，或者直接重置筛选查看全部作品
          </p>
          <button
            type="button"
            onClick={reset}
            className="inline-block px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}